import { useState, useEffect, useRef } from 'react';
import { Wifi, WifiOff, Loader2, RefreshCw, LogOut, AlertTriangle, Check } from 'lucide-react';
import Header from '../components/layout/Header.jsx';
import { useWhatsAppEstado, useWhatsAppQr, useDesconectarWhatsApp, useActualizarTrigger } from '../features/whatsapp/useWhatsApp.js';
import { useQueryClient } from '@tanstack/react-query';

const ESTADO_CONFIG = {
  open:           { label: 'Conectado',       color: 'text-emerald-600', bg: 'bg-emerald-50',  border: 'border-emerald-200', Icon: Wifi },
  connecting:     { label: 'Conectando...',   color: 'text-amber-600',   bg: 'bg-amber-50',    border: 'border-amber-200',   Icon: Loader2 },
  close:          { label: 'Desconectado',    color: 'text-red-600',     bg: 'bg-red-50',      border: 'border-red-200',     Icon: WifiOff },
  no_configurado: { label: 'Sin configurar',  color: 'text-gray-500',    bg: 'bg-gray-50',     border: 'border-gray-200',    Icon: AlertTriangle },
  error:          { label: 'Error',           color: 'text-red-600',     bg: 'bg-red-50',      border: 'border-red-200',     Icon: AlertTriangle },
};

const QR_TTL = 50; // segundos antes de refrescar el QR automáticamente

export default function WhatsAppPage() {
  const [modalAbierto, setModalAbierto] = useState(false);
  const [confirmarDesconectar, setConfirmarDesconectar] = useState(false);

  const { data: estadoData, isLoading: cargandoEstado, refetch: refetchEstado } = useWhatsAppEstado({
    refetchInterval: modalAbierto ? 3_000 : 30_000,
  });

  const estado = estadoData?.estado ?? 'error';
  const estadoCfg = ESTADO_CONFIG[estado] ?? ESTADO_CONFIG.error;

  // Cuando se detecta conexión exitosa, cerrar modal
  useEffect(() => {
    if (estado === 'open' && modalAbierto) {
      setModalAbierto(false);
    }
  }, [estado, modalAbierto]);

  return (
    <div>
      <Header title="WhatsApp" />
      <div className="p-6 flex flex-col gap-5 max-w-xl">

        {/* Card de estado */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 flex flex-col gap-5">
          <div>
            <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-3">Estado de conexión</p>
            {cargandoEstado ? (
              <div className="flex items-center gap-2 text-gray-400 text-sm">
                <Loader2 size={16} className="animate-spin" />
                Verificando...
              </div>
            ) : (
              <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md border text-sm font-medium ${estadoCfg.bg} ${estadoCfg.border} ${estadoCfg.color}`}>
                <estadoCfg.Icon size={15} className={estado === 'connecting' ? 'animate-spin' : ''} />
                {estadoCfg.label}
              </div>
            )}
            {estadoData?.detalle && (
              <p className="text-xs text-gray-400 mt-2">{estadoData.detalle}</p>
            )}
          </div>

          {estado === 'no_configurado' && (
            <p className="text-sm text-gray-500 bg-gray-50 rounded-md p-3 border border-gray-200">
              Configurá <code className="text-xs bg-gray-100 px-1 rounded">WHATSAPP_INSTANCE_NAME</code> en el servidor con el nombre de la instancia en Evolution API.
            </p>
          )}

          {/* Acciones */}
          <div className="flex gap-3">
            {(estado === 'close' || estado === 'connecting' || estado === 'error') && (
              <button
                onClick={() => setModalAbierto(true)}
                className="bg-[#0D2B5C] hover:bg-[#0a2248] text-white text-sm font-medium px-4 py-2 rounded-md transition-colors flex items-center gap-2"
              >
                <Wifi size={15} />
                Conectar WhatsApp
              </button>
            )}

            <button
              onClick={() => refetchEstado()}
              className="border border-gray-200 text-gray-500 hover:text-gray-700 text-sm px-3 py-2 rounded-md transition-colors flex items-center gap-2"
            >
              <RefreshCw size={14} />
              Actualizar
            </button>

            {estado === 'open' && !confirmarDesconectar && (
              <button
                onClick={() => setConfirmarDesconectar(true)}
                className="text-red-500 hover:text-red-700 text-sm px-3 py-2 rounded-md transition-colors flex items-center gap-2 ml-auto"
              >
                <LogOut size={14} />
                Desconectar
              </button>
            )}

            {confirmarDesconectar && (
              <ConfirmarDesconectar
                onCancel={() => setConfirmarDesconectar(false)}
                onConfirm={() => setConfirmarDesconectar(false)}
              />
            )}
          </div>
        </div>

        {/* Mensaje activador */}
        <MensajeActivador textoActual={estadoData?.textoActivador ?? ''} />

      </div>

      {modalAbierto && (
        <QrModal onClose={() => setModalAbierto(false)} />
      )}
    </div>
  );
}

function QrModal({ onClose }) {
  const { data: qrData, isFetching, refetch } = useWhatsAppQr();
  const [countdown, setCountdown] = useState(QR_TTL);
  const intervalRef = useRef(null);
  const retryRef = useRef(null);

  // Cargar QR al abrir
  useEffect(() => { refetch(); }, []);

  // Auto-retry cada 5s si no hay QR (ej: instancia en estado 'connecting' aún inicializando)
  useEffect(() => {
    if (qrData?.qr || isFetching) {
      clearInterval(retryRef.current);
      return;
    }
    retryRef.current = setInterval(() => refetch(), 5_000);
    return () => clearInterval(retryRef.current);
  }, [qrData?.qr, isFetching]);

  // Countdown + auto-refresh
  useEffect(() => {
    if (!qrData?.qr) return;
    setCountdown(QR_TTL);
    intervalRef.current = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) { refetch(); return QR_TTL; }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(intervalRef.current);
  }, [qrData?.qr]);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-sm p-6 flex flex-col gap-5">
        <div className="flex items-center justify-between">
          <h2 className="font-['Raleway'] font-bold text-[#0D2B5C] text-lg">Conectar WhatsApp</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
        </div>

        <p className="text-sm text-gray-500">
          Abrí WhatsApp en el teléfono de ISADA → <strong>Dispositivos vinculados</strong> → <strong>Vincular dispositivo</strong> → escaneá este QR.
        </p>

        <div className="flex flex-col items-center gap-3">
          {isFetching && !qrData?.qr ? (
            <div className="flex items-center gap-2 text-gray-400 text-sm py-12">
              <Loader2 size={18} className="animate-spin" />
              Generando QR...
            </div>
          ) : qrData?.qr ? (
            <>
              <img
                src={qrData.qr}
                alt="QR WhatsApp"
                className="w-52 h-52 rounded-md border border-gray-200"
              />
              <div className="flex items-center gap-2 text-xs text-gray-400">
                <span>Se renueva en {countdown}s</span>
                <button
                  onClick={() => refetch()}
                  disabled={isFetching}
                  className="text-[#0D2B5C] hover:underline disabled:opacity-40"
                >
                  {isFetching ? 'Actualizando...' : 'Refrescar'}
                </button>
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center gap-2 py-8 text-gray-400">
              <AlertTriangle size={24} />
              <p className="text-sm">No se pudo obtener el QR</p>
              <p className="text-xs text-gray-400">Reintentando automáticamente...</p>
              <button
                onClick={() => refetch()}
                disabled={isFetching}
                className="text-sm text-[#0D2B5C] hover:underline disabled:opacity-40"
              >
                {isFetching ? 'Buscando...' : 'Reintentar ahora'}
              </button>
            </div>
          )}
        </div>

        <p className="text-xs text-gray-400 text-center">
          Esperando que escanees el QR… El modal se cerrará automáticamente al conectar.
        </p>
      </div>
    </div>
  );
}

function MensajeActivador({ textoActual }) {
  const [texto, setTexto] = useState(textoActual);
  const [guardado, setGuardado] = useState(false);
  const actualizar = useActualizarTrigger();

  useEffect(() => { setTexto(textoActual); }, [textoActual]);

  function handleGuardar() {
    actualizar.mutate(texto, {
      onSuccess: () => {
        setGuardado(true);
        setTimeout(() => setGuardado(false), 2000);
      },
    });
  }

  const modificado = texto !== textoActual && texto.trim().length > 0;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5 flex flex-col gap-3">
      <div>
        <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Mensaje activador</p>
        <p className="text-xs text-gray-400">El sistema crea un lead cuando un asesor envía este mensaje al prospecto.</p>
      </div>
      <textarea
        value={texto}
        onChange={(e) => { setTexto(e.target.value); setGuardado(false); }}
        rows={2}
        className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm text-gray-700 resize-none focus:outline-none focus:border-[#0D2B5C] transition-colors"
      />
      <div className="flex items-center gap-3">
        <button
          onClick={handleGuardar}
          disabled={!modificado || actualizar.isPending}
          className="bg-[#0D2B5C] hover:bg-[#0a2248] disabled:opacity-40 text-white text-sm font-medium px-4 py-2 rounded-md transition-colors flex items-center gap-2"
        >
          {actualizar.isPending ? <Loader2 size={14} className="animate-spin" /> : guardado ? <Check size={14} /> : null}
          {guardado ? 'Guardado' : 'Guardar'}
        </button>
        {actualizar.error && (
          <p className="text-xs text-red-500">{actualizar.error?.response?.data?.error ?? 'Error al guardar'}</p>
        )}
      </div>
    </div>
  );
}

function ConfirmarDesconectar({ onCancel, onConfirm }) {
  const desconectar = useDesconectarWhatsApp();

  function handleConfirm() {
    desconectar.mutate(undefined, { onSuccess: onConfirm });
  }

  return (
    <div className="flex items-center gap-2 ml-auto">
      <span className="text-sm text-gray-500">¿Desconectar WhatsApp?</span>
      <button
        onClick={handleConfirm}
        disabled={desconectar.isPending}
        className="bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white text-sm px-3 py-1.5 rounded-md transition-colors"
      >
        {desconectar.isPending ? 'Desconectando...' : 'Confirmar'}
      </button>
      <button onClick={onCancel} className="text-gray-400 hover:text-gray-600 text-sm">
        Cancelar
      </button>
    </div>
  );
}
